/**
 * Utah chapter media + lockup copy.
 *
 * Local sources live under `assets/utah/` (`*.orig.*` kept out of the sync).
 * Re-sync public encodes with:
 *   aws --profile voxmedia s3 sync \
 *     apps/duet-csk-theverge/src/stories/data-centers-hub/assets/utah/ \
 *     s3://assets.sbnation.com/csk/uploads/verge-features/data-centers/data-centers-hub/utah/ \
 *     --acl public-read --exclude "*" \
 *     --include "*.webp" --include "*.mp4" --include "*.svg" \
 *     --exclude "*.orig.*"
 */

import { hubS3Asset } from "@story/data-centers-hub/config/hubS3";
import type { TitleMedia } from "@story/data-centers-hub/config/titleAssets";

/** Bust browser/S3 caches after replacing files at the same keys. */
const V = "20261026c";

export function hubUtahAsset(path: string): string {
  return hubS3Asset("utah", path, V);
}

/** Utah orange — package nav + chapter chrome once the wipe is open. */
export const UTAH_ORANGE = "#F96644";

/** WP node for the Utah feature — share / comment meta on the chapter plate. */
export const UTAH_FEATURE_NODE_ID = "dmcyOmN1c3RvbVN0b3J5Ojk5MzM0Mw==";

/**
 * Lockup fallbacks when the Utah WP node is missing. Live values come from
 * `authors` / `additionalBylines` / `dek.plaintext` via `resolveLockupCopy`.
 */
export const utahCopy = {
  bylineLabel: "by",
  bylineName: "Josh Dzieza",
  photoBylineLabel: "photos by",
  photoBylineName: "Stephen Voss",
  lede: "Mr. Wonderful wanted to build the world’s largest data center. The neighbors had other ideas.",
  hedAlt: "The People of Utah vs. Kevin O’Leary",
  shareTitle: "The People of Utah vs. Kevin O’Leary",
} as const;

export const utahSlideMedia = {
  /** Full-stage underlay — Stratos desert loop. */
  stratos: {
    kind: "video",
    src: hubUtahAsset("stratos.mp4"),
    poster: hubUtahAsset("stratos-poster.webp"),
    srcMobile: hubUtahAsset("stratos-mobile.mp4"),
    posterMobile: hubUtahAsset("stratos-mobile-poster.webp"),
  },
  hedPeople: hubUtahAsset("hed-people.svg"),
  /** Right column — Getty O’Leary portrait loop. */
  getty: {
    kind: "video",
    src: hubUtahAsset("getty.mp4"),
    poster: hubUtahAsset("getty-poster.webp"),
    srcMobile: hubUtahAsset("getty-mobile.mp4"),
    posterMobile: hubUtahAsset("getty-mobile-poster.webp"),
  },
  hedOleary: hubUtahAsset("hed-oleary.svg"),
  /** Left mid cell — residents still. */
  people: {
    kind: "image",
    src: hubUtahAsset("people.webp"),
  },
  /** Bottom-left cell — construction / site still under the byline plate. */
  site: {
    kind: "image",
    src: hubUtahAsset("site.webp"),
  },
} as const satisfies {
  stratos: TitleMedia;
  hedPeople: string;
  getty: TitleMedia;
  hedOleary: string;
  people: TitleMedia;
  site: TitleMedia;
};
